import { ElementProps } from '@/components/sections/builder/types';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import clsx from 'clsx';
import { ProductDetailTitleElementDataType } from '@/components/sections/builder/elements/body/product-detail/type';
import { Currency } from 'lucide-react';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/vanilla/shallow';
import { IProduct, IProductDetailPriceBook, ProductItem } from '@/types/product';
import { cn, formatIDR } from '@/lib/utils';
import React from 'react';
import { useProductStore } from '@/stores/product';
import ModalPromotionProduct from '@/components/sections/builder/elements/body/product-detail/components/modal-promotion-product';
import { useGetListProductPromotions } from '@/hooks/api/storefront/promotion';
import { useCartProduct } from '@/stores/cart';
import { useProductDetailPriceBook } from '@/hooks/api/storefront/product-detail';
import { useParams } from 'next/navigation';
import Cookies from 'js-cookie';
import PriceBookIcon from '@/components/icons/price-book-icon';
import useModal from '@/hooks/useModal';
import ModalPricebookInformation from '@/components/sections/builder/elements/body/product-detail/components/modal-pricebook-information';
import InputSelect from '@/components/sections/builder/settings/input-select';

const defaultData: ProductDetailTitleElementDataType = {
  name: 'Price',
  label: '10000',
};

export const ElementContent = ({ element }: ElementProps) => {
  const { slug: productSlug } = useParams<{ slug: string }>();
  const customerToken = Cookies.get('accessToken') as string;
  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;
  const parentData = (element.parentData || {}) as IProduct;
  const themeContent = parentData?.theme as ThemeType;

  const productStore = useProductStore((state) => state, shallow);
  const cartProduct = useCartProduct((state) => state, shallow);
  const getMinMaxPrice = (productItems: ProductItem[]) => {
    const prices = productItems
      ?.map((item) => item.sell_price)
      .filter((price) => typeof price === 'number' && !isNaN(price)) as number[];

    if (!prices || prices.length === 0) return { minPrice: null, maxPrice: null };
    return { minPrice: Math.min(...prices), maxPrice: Math.max(...prices) };
  };

  const { minPrice, maxPrice } = getMinMaxPrice(parentData.product_items || []);
  const modalPricebookInformation = useModal();

  const { data: pricebookData } = useProductDetailPriceBook(productSlug, customerToken as string, {
    enabled: !!productSlug,
  });

  const calculatePrice = () => {
    if ((parentData.variants?.length ?? 0) > 0) {
      if (minPrice !== maxPrice) {
        return productStore.price
          ? formatIDR(productStore.price)
          : `${formatIDR(minPrice as number)} - ${formatIDR(maxPrice as number)}`;
      }
      return formatIDR(minPrice as number);
    } else {
      return formatIDR(parentData.product_items?.[0].sell_price as number);
    }
  };

  const calculateDiscountPrice = () => {
    if ((parentData.variants?.length ?? 0) > 0) {
      return productStore.discountPrice;
    }
    return parentData.discount_price;
  };

  const price = calculatePrice();
  const discountPrice = calculateDiscountPrice();

  const { data: listPromoProduct } = useGetListProductPromotions(
    {
      slug: typeof parentData.slug === 'string' ? parentData.slug : '',
      token: typeof parentData.token === 'string' ? parentData.token : '',
    },
    {
      enabled: !!parentData.slug,
    }
  );

  const cartQty = cartProduct.quantity;

  const pricebook = pricebookData || [];

  const item_id = cartProduct.item_id || parentData?.product_items?.[0]?.item_id;

  const getRetailPriceFromPricebook = () => {
    const matchingItems = pricebook.filter(
      (item) => item.item_id === item_id && item.min_unit <= cartQty
    );

    if (matchingItems.length === 0) return null;

    const bestMatch = matchingItems.reduce((prev, current) =>
      current.min_unit > prev.min_unit ? current : prev
    );

    return bestMatch.retail_price;
  };

  const matchedRetailPrice = getRetailPriceFromPricebook();

  const calculateFinalPrice = (
    matchedRetailPrice: number | null,
    discountPrice: number,
    originalPrice: string | number
  ) => {
    if (!matchedRetailPrice) {
      return discountPrice !== 0 ? formatIDR(discountPrice as number) : originalPrice ?? 0;
    }

    if (discountPrice !== 0) {
      const finalPrice = discountPrice < matchedRetailPrice ? discountPrice : matchedRetailPrice;

      return formatIDR(finalPrice);
    }

    return formatIDR(matchedRetailPrice);
  };

  const finalPrice = calculateFinalPrice(matchedRetailPrice, discountPrice ?? 0, price);

  const getBasePrice = () => {
    const baseUnitItem = pricebook.find((item) => item.item_id === item_id && item.min_unit === 1);

    if (baseUnitItem) {
      return baseUnitItem.retail_price;
    }
    return parentData.product_items?.find((item) => item.item_id === item_id)?.sell_price || 0;
  };

  const basePrice = getBasePrice();

  const currentDiscountPrice = discountPrice || 0;
  const currentPricebook = pricebook.filter((item) => item.item_id === item_id);
  const getApplicablePricebook = (
    discount_price: number,
    price_book: IProductDetailPriceBook[],
    total_qty: number
  ) => {
    // Find pricebook
    const applicablePricebooks = price_book
      .filter((pb) => total_qty >= pb.min_unit)
      .sort((a, b) => b.min_unit - a.min_unit);

    if (applicablePricebooks.length === 0) {
      return null;
    }

    const discountPrice = discount_price;
    const selectedRetailPrice = applicablePricebooks[0].retail_price;

    // Check if any discount price
    if (discountPrice === 0 || selectedRetailPrice < discountPrice) {
      return applicablePricebooks[0];
    }

    return null;
  };

  const setPricebookState = (
    discount_price: number,
    price_book: IProductDetailPriceBook[],
    quantity: number
  ) => {
    let isApplicable = false;

    const hasPricebook = price_book.length > 0;

    if (hasPricebook) {
      const pricebook = getApplicablePricebook(discount_price, price_book, quantity);

      if (pricebook) {
        // Compare pricebook retail_price with discount_price
        if (discount_price && pricebook.retail_price >= discount_price) {
          // If pricebook price is higher or equal to discount price, use discount price
          isApplicable = false; // Don't use pricebook if discount is better
        } else {
          // If pricebook price is lower than discount price, use pricebook
          isApplicable = true;
        }
      }
    }

    // Set state management
    productStore.setIsApplicablePricebook(isApplicable);
  };

  // Call function to set state only
  setPricebookState(currentDiscountPrice, currentPricebook, cartQty);

  const getFilteredPricebook = () => {
    if (!cartProduct.item_id) {
      return pricebook;
    }
    return pricebook.filter((item) => item.item_id === cartProduct.item_id);
  };

  const filteredPricebook = getFilteredPricebook();

  return (
    <>
      <div data-animation='true' {...getOverrideAnimationParams(data.animation)}>
        <div className={clsx(discountPrice !== 0 && 'flex flex-col items-start')}>
          <span
            className={cn(
              'font-bold',
              data.textStyle === 'body' && 'text-lg md:text-xl lg:text-2xl',
              (data.textStyle === 'subtitle' || !data.textStyle) &&
                'text-base md:text-lg lg:text-xl',
              data.textStyle === 'uppercase' && 'text-base uppercase md:text-lg lg:text-xl',
              discountPrice !== 0 ? 'text_product_secondary' : 'text_product_primary'
            )}
          >
            {finalPrice}
          </span>
          {discountPrice !== 0 && productStore.isApplicablePricebook === false ? (
            <span className='text-[10px] font-normal text-[#C2C2C2] line-through sm:text-sm'>
              {price}
            </span>
          ) : null}
        </div>
        {pricebook.length > 0 && (
          <div className=''>
            <div className='mb-4 mt-4 flex items-center gap-x-2'>
              <PriceBookIcon className='h-5 w-5 text-black' color='black' />
              <label className='text-sm font-medium '>Tiered Pricing</label>
            </div>

            <div className='mt-2 flex flex-col gap-y-2'>
              {filteredPricebook
                .slice()
                .sort((a, b) => a.min_unit - b.min_unit)
                .slice(0, 5)
                .map((item, index) => {
                  const currentBasePrice = item_id
                    ? basePrice
                    : parentData.product_items?.find((pi) => pi.item_id === item.item_id)
                        ?.sell_price || 0;

                  const total = item.retail_price * item.min_unit;
                  const originalTotal = currentBasePrice * item.min_unit;
                  const saved = originalTotal - total;

                  return (
                    <div key={index} className='rounded-sm border border-slate-400'>
                      <div className='grid grid-cols-2 p-1'>
                        <div className='flex flex-col p-1'>
                          <h1 className='font-semibold '>Total {formatIDR(total)}</h1>
                          <div className='flex flex-col gap-x-1 lg:flex-row lg:items-center'>
                            {saved > 0 && (
                              <h1 className='font-medium text-red-500 '>
                                Saved {formatIDR(saved)}
                              </h1>
                            )}
                            <h1 className='text-slate-500 line-through'>
                              {formatIDR(originalTotal)}
                            </h1>
                          </div>
                        </div>
                        <div className='p-1 text-right'>
                          <h1 className='font-semibold text-green-500 '>
                            Buy {item.min_unit} or More
                          </h1>
                          <div className='flex flex-col gap-x-2 lg:flex-row lg:justify-end lg:text-right'>
                            <h1 className='order-1 font-medium md:order-1'>
                              {formatIDR(item.retail_price)} / Each
                            </h1>
                            <h1 className='order-2 text-slate-500 line-through lg:order-2'>
                              {formatIDR(currentBasePrice)}
                            </h1>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            {filteredPricebook.length > 5 && (
              <div className='flex justify-end py-2'>
                <button className='text-xs' onClick={() => modalPricebookInformation.open()}>
                  see more
                </button>
              </div>
            )}
          </div>
        )}

        <ModalPricebookInformation
          openModal={modalPricebookInformation.isShow}
          setOpenModal={modalPricebookInformation.setIsShow}
          pricebookData={pricebook}
          basePrice={basePrice}
          itemId={item_id}
          parentData={parentData}
          formatIDR={formatIDR}
          fileredData={filteredPricebook || []}
        />
      </div>

      {listPromoProduct && listPromoProduct.length > 0 ? (
        <div className='mt-2'>
          <ModalPromotionProduct
            listPromotion={listPromoProduct}
            theme={themeContent.promotion?.colorScheme}
          />
        </div>
      ) : null}
    </>
  );
};

export const ElementPreviewIcon = Currency;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  return (
    <React.Fragment>
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle || 'subtitle'}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              textStyle: v,
            })
          }
        />
      </div>
    </React.Fragment>
  );
}

const ProductDetailPrice = {
  type: 'price',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailPrice;
